import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const ACCESS_TOKEN_KEY = "emlak_access_token";

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const setAccessToken = (token) => {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
};
export const clearAccessToken = () => localStorage.removeItem(ACCESS_TOKEN_KEY);

export const http = axios.create({ baseURL: API, timeout: 20000 });

http.interceptors.request.use((cfg) => {
  const token = getAccessToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

class ApiError extends Error {
  constructor(message) {
    super(message || "Xəta baş verdi.");
    this.name = "ApiError";
  }
}
const checkSuccess = (body) => {
  if (body && body.success === false) throw new ApiError(body.message);
  return body;
};
const unwrap = (res) => checkSuccess(res.data)?.data;
const raw = (res) => checkSuccess(res.data);

export const login = (phoneNumber, password) =>
  http.post("/Auth/login", { phoneNumber, password }).then(unwrap);

export const refreshAccessToken = () => http.get("/Auth/refresh/token").then(unwrap);

export const fetchLoginInfo = () => http.get("/Auth/loginInfo").then(unwrap);

export const logout = () => http.post("/Auth/logout").then(raw);

let refreshPromise = null;
const NO_REFRESH_URLS = ["/Auth/refresh/token", "/Auth/login", "/Auth/logout"];

const dispatchSessionExpired = () => {
  clearAccessToken();
  window.dispatchEvent(new Event("auth:session-expired"));
};

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    const isAuthUrl = NO_REFRESH_URLS.some((u) => originalRequest.url?.includes(u));
    if (isAuthUrl || originalRequest._retry) {
      if (!isAuthUrl) dispatchSessionExpired();
      return Promise.reject(error);
    }

    if (!getAccessToken()) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const data = await refreshPromise;
      if (!data?.accessToken) {
        dispatchSessionExpired();
        return Promise.reject(error);
      }

      setAccessToken(data.accessToken);
      window.dispatchEvent(new CustomEvent("auth:token-refreshed", { detail: data.accessToken }));

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
      return http(originalRequest);
    } catch (refreshError) {
      dispatchSessionExpired();
      return Promise.reject(refreshError);
    }
  }
);

export const fetchAdjacentPropertyImage = (idProperty, direction, url) =>
  http
    .get("/Propertys/getSinglePropertyPhoto", { params: { idProperty, direction, url } })
    .then(unwrap);

export const fetchCombos = () => http.get("/Helper/combos").then(unwrap);

export const fetchPropertiesPaged = (pageIndex, pageSize, comboRequest = {}) =>
  http.post(`/Propertys/${pageIndex}/${pageSize}`, comboRequest).then(unwrap);

export const fetchPropertiesKeyset = (pageSize, lastId, comboRequest = {}) =>
  http
    .post(`/Propertys/keyset/${pageSize}`, comboRequest, { params: lastId ? { lastId } : {} })
    .then((r) => r.data?.data || { data: [], nextCursor: null });

export const fetchAllProperties = () => http.get("/Propertys").then(unwrap);

export const createProperty = (dto) => http.post("/Propertys", dto).then(raw);

export const fetchProperty = (id) => http.get(`/Propertys/${id}`).then(unwrap);

export const updateProperty = (id, dto) => http.put(`/Propertys/${id}`, dto).then(raw);

export const fetchUserPackageStatus = () => http.get("/Users/package/status").then(unwrap);

export const deleteProperty = (id) => http.delete(`/Propertys/${id}`).then(raw);

export const fetchPropertyByCode = (propertyCode) =>
  http.get(`/Propertys/byCode/${propertyCode}`).then(unwrap);

export const updatePropertyViewCount = (id) =>
  http.put(`/Propertys/updatePropertyViewCount/${id}`).then(raw);

export const fetchMonthlyPriceStats = (id) =>
  http.get(`/Propertys/getMonthlyPriceStats/${id}`).then(unwrap);

export const fetchNewPropertyNotification = (lastPropertyId, isFirst) =>
  http
    .get(`/Propertys/getNewPropertyNotification/${lastPropertyId}`, { params: { isFirst } })
    .then(unwrap);

export const fetchPropertyViewHeatLevel = () =>
  http.get("/Propertys/getPropertyViewHeatLevel").then(unwrap);

export const downloadAllProperties = (codes) =>
  http.post("/Propertys/download-all", codes).then(unwrap);

export const updatePropertyLinkVisitCount = (idProperty) =>
  http.put(`/Propertys/updatePropertyLinkVisitCount/${idProperty}`).then(unwrap);

export const FAVORITE_COLOR = "#F44336";

export const addPropertyColor = (fkIdProperty, color = FAVORITE_COLOR) =>
  http.post("/Propertys/color", { fkIdProperty, color }).then(raw);

export const removePropertyColor = (idProperty) =>
  http.delete(`/Propertys/color/${idProperty}`).then(raw);

export const changePropertyColor = (idProperty, color) =>
  http.put(`/Propertys/color/${idProperty}`, null, { params: { color } }).then(raw);

export const fetchFiltersPaged = (pageIndex, pageSize) =>
  http.post(`/Filters/${pageIndex}/${pageSize}`).then(unwrap);

export const fetchFilters = () => http.get("/Filters").then(unwrap);

export const createFilter = (dto) => http.post("/Filters", dto).then(unwrap);

export const fetchFilterById = (id) => http.get(`/Filters/${id}`).then(unwrap);

export const deleteFilter = (id) => http.delete(`/Filters/${id}`).then(raw);

export const fetchPackages = () => http.get("/Packages").then(unwrap);

export const fetchFeedBackTypes = () => http.get("/FeedBackTypes/feedback-types").then(unwrap);

export const createPropertyFeedBack = (fkIdProperty, fkIdFeedbackType) =>
  http.post("/PropertyFeedBack", { fkIdProperty, fkIdFeedbackType }).then(unwrap);

export const fetchUserPaymentsPaged = (pageIndex, pageSize) =>
  http.get(`/UserPayments/${pageIndex}/${pageSize}`).then(unwrap);

export const fetchUserDebt = (dto) => http.post("/UserPayments/getUserDebt", dto).then(raw);

export const insertUserPayment = (dto) => http.post("/UserPayments/insertUserPayment", dto).then(raw);

export const fetchUsersPaged = (pageIndex, pageSize) =>
  http.get(`/Users/${pageIndex}/${pageSize}`).then(unwrap);

export const fetchUserReferenceStatus = () => http.get("/Users/userReferanceStatus").then(unwrap);

export const fetchUsers = () => http.get("/Users").then(unwrap);
export const fetchCustomPropertyNote = (propertyId) =>
  http.get(`/Propertys/getCustomPropertyNote/${propertyId}`).then(unwrap);

export const saveCustomPropertyNote = (propertyId, note) =>
  http.post("/Propertys/SaveCustomPropertyNote", null, {
    params: { propertyid: propertyId, note },
  }).then(raw);
export const removeCustomPropertyNote = (propertyId) =>
  http
    .post("/Propertys/RemoveCustomPropertyNote", null, {
      params: { propertyid: propertyId },
    })
    .then(raw);
export const createUser = (phoneNumber, referralId) =>
  http.post("/Users", { phoneNumber, referralId }).then(raw);

export const updateViewMode = (mode) => http.put(`/Users/viewMode/${mode}`).then(unwrap);

export const fetchProfile = () => http.get("/Users/profile").then(unwrap);

export const fetchUser = (id) => http.get(`/Users/${id}`).then(unwrap);

export const updateUser = (id, dto) => http.put(`/Users/${id}`, dto).then(raw);

export const deleteUser = (id) => http.delete(`/Users/${id}`).then(raw);

export const resetPassword = (password, passwordConfirmation) =>
  http.post("/Users/resetPassword", { password, passwordConfirmation }).then(raw);

export const resetForgotPassword = (phoneNumber) =>
  http.post("/Users/resetForgotPassword", { phoneNumber }).then(raw);

export const resetPasswordById = (id, password, passwordConfirmation) =>
  http.post(`/Users/${id}/resetPassword`, { password, passwordConfirmation }).then(raw);

export const fetchUserCode = () => http.get("/Users/code").then(unwrap);

const VIEW_HEAT_LEVEL_KEY = "emlak_view_heat_level";

export const getCachedViewHeatLevel = () => {
  try {
    return JSON.parse(localStorage.getItem(VIEW_HEAT_LEVEL_KEY) || "null");
  } catch {
    return null;
  }
};

export const cacheViewHeatLevel = (data) => {
  try {
    localStorage.setItem(VIEW_HEAT_LEVEL_KEY, JSON.stringify(data));
  } catch { }
};

const COMBO_ARRAY_KEYS = ["idPropertyType", "regionIds", "metroIds", "roomIds", "buildingTypeIds", "repairRateIds", "targetIds"];
const COMBO_INT_KEYS = ["idOwnerType", "idOperationType", "idDocument", "floorMin", "floorMax", "buildingfloorMin", "buildingfloorMax", "isOnlyExtract", "isOnlyLoan", "isNotLastFloor", "hot"];
const COMBO_NUM_KEYS = ["priceMin", "priceMax", "areaMin", "areaMax", "generalAreaMin", "generalAreaMax", "landAreaMin", "landAreaMax"];

export const SORT_TYPE_MAP = {
  newest: "dateDesc",
  oldest: "dateAsc",
  price_asc: "priceAsc",
  price_desc: "priceDesc",
};

export const toComboRequest = (filters = {}, extra = {}) => {
  const body = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === "" || v == null) continue;
    if (COMBO_ARRAY_KEYS.includes(k)) {
      const ids = String(v)
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n));
      if (ids.length) body[k] = ids;
    } else if (COMBO_INT_KEYS.includes(k)) body[k] = Number(v);
    else if (COMBO_NUM_KEYS.includes(k)) body[k] = Number(v);
    else body[k] = v;
  }

  const mappedExtra = { ...extra };
  if (mappedExtra.sortType) {
    mappedExtra.sortType = SORT_TYPE_MAP[mappedExtra.sortType] || mappedExtra.sortType;
  }

  return { ...body, ...mappedExtra };
};